import Mutator from '@magnus/react-native-mutator';
//@ts-ignore
import Storage from '@magnus/react-native-mutator/src/storage';

import Analytics from '@web-solutions/module-analytics';

import createRemoteConfigSnapshotNormalizer from './normalizer';

let _defaultValues: object;

/**
 * Backend contract for `experiments` on `POST /v2/user` / `PUT /user/update-by-token`:
 * up to 10 ids per request, each up to 120 chars, only letters, digits and
 * `_ / . - :` (`,` cuts lists in the report filters, `|`, spaces and newlines
 * are rejected). An id that breaks the rules is silently dropped server-side
 * and never reaches the cohort report, so names are normalised here instead.
 */
const EXPERIMENT_ID_MAX_COUNT = 10;
const EXPERIMENT_ID_MAX_LENGTH = 120;
const EXPERIMENT_ID_FORBIDDEN = /[^A-Za-z0-9_/.:-]+/g;
const EXPERIMENT_ID_SEPARATOR = '_';

/**
 * Turns a Magnus name into something the backend accepts: every run of
 * forbidden characters becomes one `_`, repeated `_` collapse, leading/trailing
 * `_` go away. `0.99+19.99(week) vs 0.99+39.99(month)` → `0.99_19.99_week_vs_0.99_39.99_month`.
 */
export function normalizeExperimentIdPart(name: string): string {
  return String(name ?? '')
    .replace(EXPERIMENT_ID_FORBIDDEN, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Cohort-report ids of the experiment groups this user is currently in, built as
 * `<experiment name>_<variant name>` from the names in Magnus, each part
 * normalised (e.g. experiment `MCX_PRICE_25/08/26` + variant `9.99_WEEK` →
 * `MCX_PRICE_25/08/26_9.99_WEEK`; experiment `lpups - copy` + variant `group B`
 * → `lpups_-_copy_group_B`). The backend does not parse the string, it is just a
 * label to group and filter by — so the report shows the normalised form, not
 * the exact Magnus spelling.
 *
 * Read from Mutator's own storage: the variants it picked (or got back from the
 * backend as `active_experiments`) joined with the experiment list from the last
 * fetch. A saved variant whose experiment is no longer running/collecting, or
 * that was `skipped`, is not an active group and is left out. Empty until the
 * first `fetch()`/`activate()` completes.
 */
export async function getActiveExperimentIds(): Promise<string[]> {
  try {
    const [cache, selected] = await Promise.all([
      Storage.getFetchCache(),
      Storage.getSelectedVariants(),
    ]);

    if (!cache?.experiments?.length || !selected) {
      return [];
    }

    const ids: string[] = [];

    for (const experiment of cache.experiments) {
      const variantId = selected[experiment.id];

      if (!variantId || variantId === 'skipped') {
        continue;
      }
      if (experiment.status !== 'running' && experiment.status !== 'collecting') {
        continue;
      }

      const variant = experiment.variants?.find((v: any) => v.id === variantId);

      if (!variant) {
        continue;
      }

      // A name with nothing allowed in it (e.g. Cyrillic only) normalises to ""
      // — fall back to the Magnus numeric id so the group still stays unique.
      const experimentPart = normalizeExperimentIdPart(experiment.name) || String(experiment.id);
      const variantPart = normalizeExperimentIdPart(variant.name) || String(variant.id);

      ids.push(
        `${experimentPart}${EXPERIMENT_ID_SEPARATOR}${variantPart}`.slice(0, EXPERIMENT_ID_MAX_LENGTH),
      );
    }

    return ids.slice(0, EXPERIMENT_ID_MAX_COUNT);
  } catch (_) {
    return [];
  }
}

const RemoteConfigModule = {
  fetch(token: string, defaultValues: object, { duration, storageKey }: { duration?: number, storageKey?: string } = {}): Promise<object> {
    Mutator.init({
      token,
      useBackendStorage: true,
      storageKey,
    });
    _defaultValues = defaultValues;
    return Promise.race([
      Mutator.fetch(duration),
      new Promise((_, reject) => setTimeout(reject, 5000)),
    ])
      .catch(() =>
        Storage.getFetchCache()
          .catch(() => null)
          .then((c: any) => {
            if (c) {
              return Promise.resolve();
            }
            return import(`./${token}.json`).then(({ default: r }) => Storage.setFetchCache(r));
          }),
      )
      .then(() => {
        if (Mutator.getSessionFirst()) {
          Analytics.trackMutatorSessionFirst();
        }
      })
      .then(() => this.activate());
  },

  activate(): Promise<object> {
    return Mutator.activate()
      .then(createRemoteConfigSnapshotNormalizer(_defaultValues))
      .then(({ remoteConfig }) => remoteConfig);
  },
};

export default RemoteConfigModule;
